import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, IconButton, Avatar, Menu, MenuItem,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Chip, Select, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Autocomplete, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  ArrowBack, ExitToApp, LocalHospital, Add, Edit, Delete, Check, Close,
} from '@mui/icons-material';
import { authService, userService, leaveService } from '../../services/api';

const LEAVE_TYPES  = ['Casual', 'Sick', 'Unpaid'];
const LEAVE_STATUS = ['Pending', 'Approved', 'Rejected'];

const LEAVE_COLOR  = { Casual: 'primary', Sick: 'warning', Unpaid: 'error' };
const STATUS_COLOR = { Pending: 'warning', Approved: 'success', Rejected: 'error' };

const fmt = d => d ? new Date(d).toLocaleDateString('en-IN') : '—';

const monthName = m => new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' });

const emptyForm = () => ({
  userId:    null,
  fromDate:  new Date().toISOString().split('T')[0],
  toDate:    new Date().toISOString().split('T')[0],
  leaveType: 'Casual',
  reason:    '',
  status:    'Approved',
  isHalfDay: false,
});

export default function LeaveManagementPage() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());

  const [staff,   setStaff]   = useState([]);
  const [leaves,  setLeaves]  = useState([]);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(emptyForm());
  const [saving,     setSaving]     = useState(false);
  const [deleteId,   setDeleteId]   = useState(null);

  const loadStaff = useCallback(async () => {
    try {
      const res = await userService.getAllUsers();
      setStaff(res.data.filter(u => u.role !== 'Admin'));
    } catch { /* ignore */ }
  }, []);

  const loadLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leaveService.getAll({ month, year });
      setLeaves(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { loadStaff(); }, [loadStaff]);
  useEffect(() => { loadLeaves(); }, [loadLeaves]);

  const handleField = f => e => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleFromDateChange = e => {
    const val = e.target.value;
    setForm(prev => ({ ...prev, fromDate: val, toDate: prev.isHalfDay ? val : prev.toDate }));
  };

  const handleHalfDayChange = e => {
    const checked = e.target.checked;
    setForm(prev => ({ ...prev, isHalfDay: checked, toDate: checked ? prev.fromDate : prev.toDate }));
  };

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = row => {
    setEditId(row.id);
    setForm({
      userId:    row.userId,
      fromDate:  row.fromDate.split('T')[0],
      toDate:    row.toDate.split('T')[0],
      leaveType: row.leaveType,
      reason:    row.reason || '',
      status:    row.status,
      isHalfDay: row.isHalfDay || false,
    });
    setDialogOpen(true);
  };

  const handleQuickStatus = async (row, status) => {
    try {
      await leaveService.update(row.id, {
        userId:    row.userId,
        fromDate:  row.fromDate,
        toDate:    row.toDate,
        leaveType: row.leaveType,
        reason:    row.reason || '',
        isHalfDay: row.isHalfDay || false,
        status,
      });
      await loadLeaves();
    } catch { /* ignore */ }
  };

  const handleSave = async () => {
    if (!form.userId) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        userId:  typeof form.userId === 'object' ? form.userId.id : form.userId,
        toDate:  form.isHalfDay ? form.fromDate : form.toDate,
      };
      if (editId) {
        await leaveService.update(editId, payload);
      } else {
        await leaveService.create(payload);
      }
      setDialogOpen(false);
      await loadLeaves();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await leaveService.delete(deleteId);
      setDeleteId(null);
      await loadLeaves();
    } catch { /* ignore */ }
  };

  const totalDays = row => {
    if (row.isHalfDay) return 0.5;
    const from = new Date(row.fromDate);
    const to   = new Date(row.toDate);
    return Math.round((to - from) / 86400000) + 1;
  };

  const staffMap = {};
  staff.forEach(s => { staffMap[s.id] = `${s.firstName} ${s.lastName}`; });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 36, mr: 1.5 }} />
          <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Leave Management
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
            <IconButton size="large" onClick={e => setAnchorEl(e.currentTarget)} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Controls */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{monthName(i + 1)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openAdd}
              sx={{ ml: 'auto' }}
            >
              Add Leave
            </Button>
          </Box>
        </Paper>

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {LEAVE_TYPES.map(t => {
            const count = leaves.filter(l => l.leaveType === t).length;
            return (
              <Chip key={t} label={`${t}: ${count}`} color={LEAVE_COLOR[t]} variant="outlined" size="small" />
            );
          })}
          {leaves.filter(l => l.status === 'Pending').length > 0 && (
            <Chip
              label={`Pending Approval: ${leaves.filter(l => l.status === 'Pending').length}`}
              color="warning"
              size="small"
            />
          )}
          <Chip label={`Total: ${leaves.length}`} variant="outlined" size="small" />
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#1565c0' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Staff</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Role</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>From</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>To</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Days</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Reason</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaves.map((row, idx) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.userName}</TableCell>
                      <TableCell>
                        <Chip label={row.role} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={row.leaveType} color={LEAVE_COLOR[row.leaveType]} size="small" />
                        {row.isHalfDay && <Chip label="½ Day" size="small" variant="outlined" sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell>{fmt(row.fromDate)}</TableCell>
                      <TableCell>{row.isHalfDay ? '—' : fmt(row.toDate)}</TableCell>
                      <TableCell align="center">{totalDays(row)}</TableCell>
                      <TableCell>
                        <Chip label={row.status} color={STATUS_COLOR[row.status]} size="small" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.reason || '—'}
                      </TableCell>
                      <TableCell align="center">
                        {row.status === 'Pending' && (
                          <>
                            <IconButton size="small" color="success" title="Approve" onClick={() => handleQuickStatus(row, 'Approved')}>
                              <Check fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" title="Reject" onClick={() => handleQuickStatus(row, 'Rejected')}>
                              <Close fontSize="small" />
                            </IconButton>
                          </>
                        )}
                        <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {leaves.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No leave records for {monthName(month)} {year}.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit Leave Record' : 'Add Leave Record'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Autocomplete
            options={staff}
            getOptionLabel={s => `${s.firstName} ${s.lastName} (${s.role})`}
            value={staff.find(s => s.id === (typeof form.userId === 'object' ? form.userId?.id : form.userId)) || null}
            onChange={(_, val) => setForm(prev => ({ ...prev, userId: val?.id || null }))}
            renderInput={params => <TextField {...params} label="Staff Member" size="small" required />}
            disabled={!!editId}
          />

          <FormControlLabel
            control={<Checkbox checked={form.isHalfDay} onChange={handleHalfDayChange} />}
            label="Half Day Leave"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={form.isHalfDay ? 'Date' : 'From Date'}
              type="date"
              size="small"
              fullWidth
              value={form.fromDate}
              onChange={handleFromDateChange}
              InputLabelProps={{ shrink: true }}
            />
            {!form.isHalfDay && (
              <TextField
                label="To Date"
                type="date"
                size="small"
                fullWidth
                value={form.toDate}
                onChange={handleField('toDate')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: form.fromDate }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Leave Type</InputLabel>
              <Select value={form.leaveType} label="Leave Type" onChange={handleField('leaveType')}>
                {LEAVE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={form.status} label="Status" onChange={handleField('status')}>
                {LEAVE_STATUS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Reason (optional)"
            size="small"
            multiline
            rows={2}
            value={form.reason}
            onChange={handleField('reason')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.userId}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {editId ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Leave Record</DialogTitle>
        <DialogContent>Are you sure you want to delete this leave record?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
